import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Paper from "@material-ui/core/Paper";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { UseMutationResult } from "@tanstack/react-query";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { PriorityCreateData } from "../../../services/api-tansquery/priority/create";
import { Priority } from "../../../@types/priority";
import IconField from "./components/icon-field";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import FormHelperText from "@material-ui/core/FormHelperText";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import { MdInfo } from "react-icons/md";
import ColorPickerInput from "../../../Components/ColorPickerInput";

type FormProps = {
    priority?: Priority;
    mutation?: UseMutationResult<
        Priority,
        unknown,
        | PriorityCreateData,
        unknown
    >
};

export default function Form({ priority, mutation }: FormProps) {
    const history = useHistory();
    const { classes } = useFormStyles();

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            nome: "",
            descricao: "",
            peso: 0,
            ativo: false,
            cor_tag: "",
            cor_fonte: "",
            sigla_senha: "",
            icone: null,
        }
    });

    useEffect(() => {
        if (priority) {
            form.reset({
                nome: priority.nome || "",
                descricao: priority.descricao || "",
                peso: priority.peso || 0,
                ativo: priority.ativo || false,
                cor_tag: priority.cor_tag?.toUpperCase() || "",
                cor_fonte: priority.cor_fonte?.toUpperCase() || "",
                sigla_senha: priority.sigla_senha || "",
                icone: priority.icone || null,
            });
        }
    }, [form, priority]);

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            nome: data.nome,
            descricao: data.descricao,
            peso: data.peso,
            ativo: data.ativo,
            cor_tag: data.cor_tag,
            cor_fonte: data.cor_fonte,
            sigla_senha: data.sigla_senha,
            icone: data.icone,
        });
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            history.goBack();
        }
    }, [mutation.isSuccess, history]);

    return (
        <Paper
            elevation={3}
            style={{ padding: 20 }}
            component="form"
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Typography className={classes.title}>
                Gerenciar Prioridade
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Controller
                        name="nome"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Nome"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Informe o nome da prioridade"}
                                InputProps={{
                                    required: false,
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="descricao"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Descrição"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Informe a descrição da prioridade"}
                                InputProps={{
                                    required: false,
                                }}
                            />
                        )}
                    />
                </Grid>
                {/* Tooltip acima da linha dos campos */}
                <Grid item xs={3}>
                    <Box display="flex" justifyContent="flex-start" ml={6}>
                        <Tooltip title="O campo peso é fundamental para definir a posição na fila do sistema. Essa fila é calculada da seguinte forma: (peso da prioridade + 1) multiplicado pelo tempo de espera. Em outras palavras, conforme o valor do peso da prioridade registrado, o sistema determina a ordem de atendimento na fila. Os valores padrão são: atendimento prioritário recebe peso 1 e atendimento para idosos com 80 anos ou mais recebe peso 2.">
                            <IconButton size="small" tabIndex={-1}>
                                <MdInfo />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Controller
                        name="peso"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                select
                                fullWidth
                                required
                                label="Peso"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Selecione o peso da prioridade"}
                                onChange={e => field.onChange(Number(e.target.value))}
                                InputProps={{
                                    required: false,
                                }}
                            >
                                {[...Array(11).keys()].map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Controller
                        name="cor_tag"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ColorPickerInput
                                {...field}
                                required
                                fullWidth
                                label="Cor da Tag"
                                variant="outlined"
                                error={!!fieldState.error}
                                value={field.value}
                                onChange={e => field.onChange(e.target.value?.toUpperCase())}
                                helperText={fieldState.error?.message || "Exemplo: #FFFFFF"}
                                style={{ marginTop: 24 }}
                                InputProps={{ required: false }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Controller
                        name="cor_fonte"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ColorPickerInput
                                {...field}
                                required
                                fullWidth
                                label="Cor da Fonte"
                                variant="outlined"
                                error={!!fieldState.error}
                                value={field.value}
                                onChange={e => field.onChange(e.target.value?.toUpperCase())}
                                helperText={fieldState.error?.message || "Exemplo: #000000"}
                                style={{ marginTop: 24 }}
                                InputProps={{ required: false }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Controller
                        name="sigla_senha"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Sigla da prioridade"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Informe a sigla que será usada como prefixo da senha"}
                                style={{ marginTop: 24 }}
                                InputProps={{
                                    required: false,
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="icone"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <IconField
                                label="Adicionar Ícone"
                                field={field}
                                fieldState={fieldState}
                                setError={form.setError}
                                clearErrors={form.clearErrors}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="ativo"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                            classes={{
                                                switchBase: classes.switchBase,
                                                checked: classes.checked,
                                                track: classes.track,
                                            }}
                                        />
                                    }
                                    label="Ativo"
                                />
                                <FormHelperText>
                                    {fieldState.error?.message || "O status da prioridade está ativo?"}
                                </FormHelperText>
                            </>
                        )}
                    />
                </Grid>
            </Grid>
            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    onClick={() => history.goBack()}
                    disabled={mutation.isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    className={classes.saveButton}
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    );
}
